<template>
  <div class="p-6 text-slate-900 dark:text-white">

    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-bold">{{ $t("cars") }} {{ $t("inventory") }}</h2>
      <div class="flex gap-2">

    <BaseButton
  type="secondary"
  @click="toggleSelectionMode"
  class="border"
  :class="selectionMode
    ? 'border-primary text-primary bg-primary/5'
    : 'border-slate-300 hover:bg-slate-50 dark:hover:bg-white/5'"
>
  <i
    :class="selectionMode ? 'fas fa-times' : 'fas fa-check-square'"
    class="mr-2"
  ></i>
  {{
    selectionMode
      ? ($t("cancel_selection") || "Cancel selection")
      : ($t("select_multiple") || "Select multiple")
  }}
</BaseButton>

<BaseButton
  v-if="isModerator"
  :type="showAllCars ? 'primary' : 'secondary'"
  @click="toggleAllCars"
  class="border border-slate-300 hover:bg-slate-50 dark:hover:bg-white/5"
>
  {{ showAllCars ? "My Cars" : "All Cars" }}
</BaseButton>

        <BaseButton
          type="secondary"
          @click="exportToExcel"
          :disabled="exporting"
          class="border border-slate-300 hover:bg-slate-50 dark:hover:bg-white/5"
        >
          <i class="fas fa-file-excel mr-2"></i>
          {{ exporting ? $t("exporting") + "..." : $t("export_excel") }}
        </BaseButton>

        <BaseButton
          type="primary"
          @click="createCar"
          class="bg-primary hover:bg-primary-hover text-white"
        >
          {{ $t("add_car") }}
        </BaseButton>
      </div>
    </div>
 

    <!-- Selection toolbar - visible any time selectionMode is on, even before anything is picked -->
    <div
      v-if="selectionMode"
      class="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30 rounded-xl p-4 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4 transition-all"
    >
      <label class="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          :checked="isAllSelected"
          :indeterminate.prop="isPartiallySelected"
          @change="toggleSelectAll"
          class="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary dark:border-white/10 dark:bg-dark-input cursor-pointer"
        />
        <span class="text-sm text-blue-700 dark:text-blue-300 font-bold">
          {{
            selectedCarIds.length > 0
              ? `${selectedCarIds.length} ${$t("cars_selected") || "cars selected"}`
              : ($t("select_all") || "Select all") + ` (${totalCars})`
          }}
        </span>
      </label>

      <div v-if="selectedCarIds.length > 0" class="flex gap-2 items-center w-full sm:w-auto">
        <BaseSelect v-model="bulkStatus" class="!w-full sm:!w-48 !py-1 !text-xs">
          <option value="" disabled>{{ $t("select_status") || "Select Status" }}</option>
          <option value="available">{{ $t("available") }}</option>
          <option value="pending_approval">{{ $t("pending_approval") }}</option>
          <option value="rented">{{ $t("rented") }}</option>
          <option value="maintenance">{{ $t("maintenance") }}</option>
          <option value="inactive">{{ $t("inactive") }}</option>
        </BaseSelect>
        <BaseButton
          type="primary"
          @click="applyBulkStatus"
          :disabled="!bulkStatus || bulkLoading"
          class="!py-1.5 !px-4 !text-xs bg-primary hover:bg-primary-hover text-white whitespace-nowrap"
        >
          {{ bulkLoading ? $t("loading") : ($t("apply") || "Apply") }}
        </BaseButton>
        <BaseButton
          type="secondary"
          @click="selectedCarIds = []"
          class="!py-1.5 !px-4 !text-xs border border-slate-300 bg-white dark:bg-dark-surface dark:border-white/5 whitespace-nowrap"
        >
          {{ $t("clear") || "Clear" }}
        </BaseButton>
      </div>
    </div>
 

    <div
      class="bg-white dark:bg-dark-surface shadow rounded p-4 mb-6 border border-slate-200 dark:border-white/5 transition-colors flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end"
    >
      <div class="flex-1">
        <SearchBar
          v-model="search"
          @search="onSearch"
          class="dark:bg-dark-input dark:text-white"
          :placeholder="$t('search') + '...'"
        />
      </div>
      <div class="w-full sm:w-56">
        <BaseSelect v-model="statusFilter" @change="onStatusFilterChange">
          <option value="">{{ $t("all_statuses") }}</option>
          <option value="available">{{ $t("available") }}</option>
          <option value="pending_approval">{{ $t("pending_approval") }}</option>
          <option value="rented">{{ $t("rented") }}</option>
          <option value="maintenance">{{ $t("maintenance") }}</option>
          <option value="inactive">{{ $t("inactive") }}</option>
        </BaseSelect>
      </div>
      <div class="w-full sm:w-56">
        <BaseSelect v-model="hasDriverFilter" @change="onHasDriverFilterChange">
          <option value="">{{ $t("all") }} ({{ $t("has_driver") }})</option>
          <option value="1">{{ $t("has_driver") }}</option>
          <option value="0">{{ $t("no_driver") }}</option>
        </BaseSelect>
      </div>
      <div class="w-full sm:w-56">
        <BaseSearchableSelect
          v-model="ownerFilter"
          :options="owners"
          @change="onOwnerFilterChange"
          :placeholder="$t('filter_by_owner')"
          label-key-en="username"
          label-key-ar="username"
        />
      </div>
      <div class="w-full sm:w-48">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          From date
          <input
            v-model="dateFrom"
            type="date"
            :max="dateTo || undefined"
            class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 outline-none transition-all focus:border-primary/50 focus:ring-2 focus:ring-primary/20 dark:border-white/5 dark:bg-dark-surface dark:text-slate-200"
            @change="onDateRangeChange"
          />
        </label>
      </div>
      <div class="w-full sm:w-48">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          To date
          <input
            v-model="dateTo"
            type="date"
            :min="dateFrom || undefined"
            class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 outline-none transition-all focus:border-primary/50 focus:ring-2 focus:ring-primary/20 dark:border-white/5 dark:bg-dark-surface dark:text-slate-200"
            @change="onDateRangeChange"
          />
        </label>
      </div>
      <div class="flex w-full gap-4 sm:w-auto">
        <div class="w-1/2 sm:w-40">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Price from (EGP)
            <input
              v-model="priceMin"
              type="number"
              min="0"
              :max="priceMax || undefined"
              class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 outline-none transition-all focus:border-primary/50 focus:ring-2 focus:ring-primary/20 dark:border-white/5 dark:bg-dark-surface dark:text-slate-200"
              @change="onPriceRangeChange"
            />
          </label>
        </div>
        <div class="w-1/2 sm:w-40">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Price to (EGP)
            <input
              v-model="priceMax"
              type="number"
              :min="priceMin || 0"
              class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 outline-none transition-all focus:border-primary/50 focus:ring-2 focus:ring-primary/20 dark:border-white/5 dark:bg-dark-surface dark:text-slate-200"
              @change="onPriceRangeChange"
            />
          </label>
        </div>
      </div>
    </div>

    <div
      class="bg-white dark:bg-dark-surface shadow rounded overflow-hidden border border-slate-200 dark:border-white/5 transition-colors"
    >
      <div v-if="loading" class="p-12 text-center">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"
        ></div>
        <p class="mt-4 text-slate-500 dark:text-slate-400">
          {{ $t("loading") }}
        </p>
      </div>

      <template v-else>
        <BaseDataTable
          :columns="columns"
          :data="cars"
          class="dark:text-slate-300"
        >
          <!-- Selection column: only rendered while selectionMode is on -->
          <template #selection_header>
            <input
              v-if="selectionMode"
              type="checkbox"
              :checked="isAllSelected"
              ref="selectAllCheckbox"
              @change="toggleSelectAll"
              class="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary dark:border-white/10 dark:bg-dark-input cursor-pointer"
            />
          </template>
          <template #selection="{ row }">
            <input
              v-if="selectionMode"
              type="checkbox"
              :value="row.id"
              v-model="selectedCarIds"
              class="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary dark:border-white/10 dark:bg-dark-input cursor-pointer"
            />
          </template>

          <!-- Model -->
          <template #model_name="{ row }">
            <router-link
              :to="carViewRoute(row.id)"
              class="font-bold text-primary hover:underline"
            >
              {{ row.model_name }}
            </router-link>
          </template>

          <!-- Images -->
          <template #images="{ row }">
            <div class="flex gap-1" v-if="row.images && row.images.length">
              <img
                v-for="(img, idx) in row.images.slice(0, 2)"
                :key="idx"
                :src="img.image_path"
                class="w-10 h-10 rounded object-cover border dark:border-white/10"
              />
            </div>
            <span v-else class="text-slate-400 text-xs">
              {{ $t("no_data") }}
            </span>
          </template>

          <!-- Brand -->
          <template #brand="{ row }">
            {{ row.brand?.name_en ?? "—" }}
          </template>

          <!-- Owner -->
          <template #user="{ row }">
            {{ row.user?.username ?? "—" }}
          </template>

          <!-- Owner phone -->
          <template #owner_phone="{ row }">
            {{ row.user?.phone ?? "—" }}
          </template>

          <!-- Moderator -->
          <template #moderator="{ row }">
            {{ row.moderator?.name ?? "—" }}
          </template>

          <!-- Price -->
          <template #price_per_day="{ row }">
            <span class="font-bold">
              {{
                row.price_per_day > 0
                  ? row.price_per_day
                  : row.price_per_week > 0
                  ? row.price_per_week
                  : row.price_per_month ?? 0
              }}
            </span>
            <span class="text-[10px] ml-1 text-slate-400 uppercase">
              {{ row.currency }}
              <span class="text-[8px] lowercase font-normal">
                {{
                  row.price_per_day > 0
                    ? "/d"
                    : row.price_per_week > 0
                    ? "/w"
                    : row.price_per_month > 0
                    ? "/m"
                    : ""
                }}
              </span>
            </span>
          </template>

          <!-- Status: editable dropdown normally, read-only badge while selecting -->
          <template #status="{ row }">
            <div class="flex items-center gap-2">
<select
  v-if="!selectionMode && row.status === 'pending_approval' && canManageCar(row)"
  :value="row.status"
  :disabled="approvalLoading[row.id]"
  @change="onStatusChange(row, $event)"
  class="px-2 py-1 rounded-md text-xs border border-slate-300 bg-white text-slate-700 dark:bg-dark-input dark:border-white/10 dark:text-white cursor-pointer font-medium"
>
                <option value="pending_approval">{{ $t("pending_approval") }}</option>
                <option value="available">{{ $t("available") }}</option>
                <option value="rented">{{ $t("rented") }}</option>
                <option value="maintenance">{{ $t("maintenance") }}</option>
                <option value="inactive">{{ $t("inactive") }}</option>
              </select>
              <span
                v-else
                class="px-2 py-1 rounded-md text-xs font-medium"
                :class="statusBadgeClass(row.status)"
              >
                {{ $t(row.status) || row.status }}
              </span>
            </div>
          </template>

          <!-- Category -->
          <template #category="{ row }">
            {{ row.category?.name_en ?? "—" }}
          </template>

          <!-- Actions -->
          <template #actions="{ row }">
            <TableActions
              :row="row"
              show-view
              :show-edit="canManageCar(row)"
              :show-delete="canManageCar(row)"
              @view="viewCar($event.id)"
              @edit="editCar($event.id)"
              @delete="deleteCar($event.id)"
            >
              <template #before>
                <button
                  v-if="canManageCar(row)"
                  @click.stop="
                    $router.push(`/cars/${row.id}/unavailable-periods`)
                  "
                  class="text-[10px] font-black uppercase tracking-widest transition-colors flex items-center gap-1.5 group whitespace-nowrap text-amber-500 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300 mr-2"
                >
                  <i
                    class="fas fa-calendar-alt transition-transform group-hover:scale-110"
                  ></i>
                  {{ $t("unavailable_periods_short") }}
                </button>
                <button
                  @click.stop="goToStatusHistory(row)"
                  class="text-[10px] font-black uppercase tracking-widest transition-colors flex items-center gap-1.5 group whitespace-nowrap text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <i
                    class="fas fa-history transition-transform group-hover:scale-110"
                  ></i>
                  {{ $t("history") || "History" }}
                </button>
              </template>
            </TableActions>
          </template>
        </BaseDataTable>

        <div
          v-if="cars.length === 0"
          class="p-12 text-center border-t border-slate-100 dark:border-white/5"
        >
          <p class="text-slate-400">{{ $t("no_data") }}</p>
        </div>

        <div
          class="p-4 border-t border-slate-100 dark:border-white/5"
          v-if="pagination"
        >
          <BasePagination
            :pagination="pagination"
            store-key="vehicles"
            @change-page="fetchCars"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/components/Button.vue";
import BaseDataTable from "@/components/BaseDataTable.vue";
import BasePagination from "@/components/BasePagination.vue";
import TableActions from "@/components/TableActions.vue";
import SearchBar from "@/components/SearchBar.vue";
import BaseSelect from "@/components/BaseSelect.vue";
import BaseSearchableSelect from "@/components/BaseSearchableSelect.vue";
import carService from "@/services/car.service";
import userService from "@/services/user.service";
import authService from "@/services/auth.service";
import { alertSuccess, alertError, alertConfirm } from "@/utils/alert";
import { usePaginationStore } from "@/stores/pagination";
import Swal from "sweetalert2";

import * as XLSX from "xlsx";

const STATUS_KEY = "cars_list_status_filter";
const SEARCH_KEY = "cars_list_search";
const HAS_DRIVER_KEY = "cars_list_has_driver_filter";
const OWNER_KEY = "cars_list_owner_filter";
const DATE_FROM_KEY = "cars_list_date_from_filter";
const DATE_TO_KEY = "cars_list_date_to_filter";
const PRICE_MIN_KEY = "cars_list_price_min_filter";
const PRICE_MAX_KEY = "cars_list_price_max_filter";

export default {
  name: "CarsList",

  components: {
    BaseButton,
    BaseDataTable,
    BasePagination,
    TableActions,
    SearchBar,
    BaseSelect,
    BaseSearchableSelect,
  },

  data() {
    return {
      cars: [],
      pagination: null,
      search: sessionStorage.getItem(SEARCH_KEY) || "",
      statusFilter: sessionStorage.getItem(STATUS_KEY) || "",
      hasDriverFilter: sessionStorage.getItem(HAS_DRIVER_KEY) || "",
      ownerFilter: sessionStorage.getItem(OWNER_KEY) || "",
      dateFrom: sessionStorage.getItem(DATE_FROM_KEY) || "",
      dateTo: sessionStorage.getItem(DATE_TO_KEY) || "",
      priceMin: sessionStorage.getItem(PRICE_MIN_KEY) || "",
      priceMax: sessionStorage.getItem(PRICE_MAX_KEY) || "",
      owners: [],
      loading: false,
      approvalLoading: {},
      showAllCars: false,
      paginationStore: null,
      columns: [],
      exporting: false,

      // State machine integration
      selectionMode: false,
      selectedCarIds: [],
      bulkStatus: "",
      bulkLoading: false,
      lastBatchUuid: null,
      lastBatchSkipped: 0,
    };
  },
  watch: {
    "$i18n.locale": {
      immediate: true,
      handler() {
        this.updateColumns();
      },
    },

    search(val) {
      if (val) {
        sessionStorage.setItem(SEARCH_KEY, val);
      } else {
        sessionStorage.removeItem(SEARCH_KEY);
      }
    },

    statusFilter(val) {
      if (val) {
        sessionStorage.setItem(STATUS_KEY, val);
      } else {
        sessionStorage.removeItem(STATUS_KEY);
      }
    },

    hasDriverFilter(val) {
      if (val) {
        sessionStorage.setItem(HAS_DRIVER_KEY, val);
      } else {
        sessionStorage.removeItem(HAS_DRIVER_KEY);
      }
    },

    ownerFilter(val) {
      if (val) {
        sessionStorage.setItem(OWNER_KEY, val);
      } else {
        sessionStorage.removeItem(OWNER_KEY);
      }
    },

 
    dateFrom(val) {
      if (val) {
        sessionStorage.setItem(DATE_FROM_KEY, val);
      } else {
        sessionStorage.removeItem(DATE_FROM_KEY);
      }
    },

    dateTo(val) {
      if (val) {
        sessionStorage.setItem(DATE_TO_KEY, val);
      } else {
        sessionStorage.removeItem(DATE_TO_KEY);
      }
    },

    priceMin(val) {
      if (val !== "") {
        sessionStorage.setItem(PRICE_MIN_KEY, val);
      } else {
        sessionStorage.removeItem(PRICE_MIN_KEY);
      }
    },

    priceMax(val) {
      if (val !== "") {
        sessionStorage.setItem(PRICE_MAX_KEY, val);
      } else {
        sessionStorage.removeItem(PRICE_MAX_KEY);
      }
    },
  },

computed: {
  totalCars() {
    return this.pagination?.total || 0;
  },

  isAllSelected() {
    return this.selectedCarIds.length === this.totalCars && this.totalCars > 0;
  },

  isPartiallySelected() {
    return (
      this.selectedCarIds.length > 0 &&
      this.selectedCarIds.length < this.totalCars
    );
  },

  isModerator() {
    const roles = authService.getUser()?.roles || [];

    return roles.includes("moderator") && !roles.includes("admin");
  },
},

  methods: {
    updateColumns() {
      this.columns = [
        { label: "", field: "selection" },
        { label: "ID", field: "id" },
        { label: this.$t("model_name"), field: "model_name" },
        { label: this.$t("owner"), field: "user" },
        { label: this.$t("phone"), field: "owner_phone" },
        { label: this.$t("moderator"), field: "moderator" },
        { label: this.$t("year"), field: "year" },
        { label: this.$t("status"), field: "status" },
        { label: this.$t("pricing"), field: "price_per_day" },
        { label: this.$t("images"), field: "images" },
        { label: this.$t("brand"), field: "brand" },
        { label: this.$t("category"), field: "category" },
        { label: this.$t("plate_number"), field: "plate_number" },
      ];
    },

    // ==================== Selection mode ====================
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        this.selectedCarIds = [];
        this.bulkStatus = "";
      }
    },

async toggleSelectAll(event) {
  if (!event.target.checked) {
    this.selectedCarIds = [];
    return;
  }

  this.loading = true;

  try {
    let allCars = [];
    let page = 1;
    let lastPage = 1;

    do {
      const res = await carService.getAll({
        per_page: 100,
        search: this.search,
        status: this.statusFilter || undefined,
        has_driver:
          this.hasDriverFilter !== ""
            ? this.hasDriverFilter
            : undefined,
        owner_id:
          this.ownerFilter !== ""
            ? this.ownerFilter
            : undefined,
        page,
      });

      const responseData = res.data;

      allCars = allCars.concat(responseData.data || []);

      lastPage = responseData.pagination?.total_pages  || 1;
      page++;
    } while (page <= lastPage);

    this.selectedCarIds = allCars.map(car => car.id);

  } finally {
    this.loading = false;
  }
} ,

    onSearch() {
      this.selectedCarIds = [];
      this.bulkStatus = "";
      this.fetchCars(1);
    },

    onStatusFilterChange() {
      this.selectedCarIds = [];
      this.bulkStatus = "";
      this.fetchCars(1);
    },

    onHasDriverFilterChange() {
      this.selectedCarIds = [];
      this.bulkStatus = "";
      this.fetchCars(1);
    },

    onOwnerFilterChange() {
      this.selectedCarIds = [];
      this.bulkStatus = "";
      this.fetchCars(1);
    },

    onDateRangeChange() {
      this.fetchCars(1);
    },

    onPriceRangeChange() {
      this.fetchCars(1);
    },

    toggleAllCars() {
      this.showAllCars = !this.showAllCars;
      this.fetchCars(1);
    },

    canManageCar(car) {
      return car.can_edit !== false;
    },

    carViewRoute(id) {
      return {
        name: "CarView",
        params: { id },
        query: this.showAllCars ? { all_cars: "true" } : {},
      };
    },

    async fetchOwners() {
      try {
        const res = await userService.getOwners();
        let ownersData = [];
        const responseData = res.data;

        if (responseData.data && Array.isArray(responseData.data)) {
          ownersData = responseData.data;
        } else if (Array.isArray(responseData)) {
          ownersData = responseData;
        }

        this.owners = ownersData
          .filter((owner) => owner.user)
 
          .map((owner) => ({
            id: owner.user.id,
            username:
              owner.user.username ||
              owner.user.full_name_en ||
              owner.user.full_name_ar,
          }));
 
      } catch (err) {
        console.error("Failed to fetch owners", err);
      }
    },

    async fetchCars(page = 1) {
      this.loading = true;

      try {
        if (this.paginationStore) {
          this.paginationStore.setPage("vehicles", page);
        }

        const res = await carService.getAll({
          per_page: 10,
          search: this.search,
          status: this.statusFilter || undefined,
          has_driver:
            this.hasDriverFilter !== "" ? this.hasDriverFilter : undefined,
          owner_id: this.ownerFilter !== "" ? this.ownerFilter : undefined,
          date_from: this.dateFrom || undefined,
          date_to: this.dateTo || undefined,
          price_min: this.priceMin !== "" ? this.priceMin : undefined,
          price_max: this.priceMax !== "" ? this.priceMax : undefined,
          all_cars: this.showAllCars || undefined,
          page,
        });

        const responseData = res.data;

        if (responseData.data && Array.isArray(responseData.data)) {
          this.cars = responseData.data;
          this.pagination = responseData.pagination || null;
        } else if (Array.isArray(responseData)) {
          this.cars = responseData;
          this.pagination = null;
        } else {
          this.cars = [];
          this.pagination = null;
        }
      } catch (err) {
        console.error(err);
        alertError("Failed to fetch cars inventory");
      } finally {
        this.loading = false;
      }
    },

    createCar() {
      this.$router.push({ name: "CarCreate" });
    },

    editCar(id) {
      this.$router.push({ name: "CarEdit", params: { id } });
    },

    viewCar(id) {
      this.$router.push(this.carViewRoute(id));
    },

    async deleteCar(id) {
      const confirm = await alertConfirm("Delete this car?");
      try {
        if (confirm.isConfirmed) {
          await carService.delete(id);
          this.cars = this.cars.filter((c) => c.id !== id);
          alertSuccess("Car deleted successfully");
        }
      } catch (err) {
        console.error(err);
      }
    },

    statusBadgeClass(status) {
      return {
        "bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-500":
          status === "available",
        "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-500":
          status === "rented" || status === "pending_approval",
        "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-500":
          status === "maintenance",
        "bg-gray-100 text-gray-700 dark:bg-slate-500/10 dark:text-slate-500":
          status === "inactive",
      };
    },

    async onStatusChange(row, event) {
      const nextStatus = event.target.value;

      if (nextStatus === row.status) return;

      const { value: reason, isDismissed } = await Swal.fire({
        title:
          this.$i18n.locale === "ar"
            ? "سبب تغيير الحالة"
            : "Reason for status change",
        input: "text",
        inputPlaceholder:
          this.$i18n.locale === "ar" ? "اكتب السبب هنا..." : "Enter reason...",
        showCancelButton: true,
        confirmButtonText: this.$i18n.locale === "ar" ? "تأكيد" : "Confirm",
        cancelButtonText: this.$i18n.locale === "ar" ? "إلغاء" : "Cancel",
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#dc2626",
      });

      if (isDismissed) {
        event.target.value = row.status;
        return;
      }

      this.approvalLoading = { ...this.approvalLoading, [row.id]: true };

      try {
        await carService.changeStatus(row.id, nextStatus, reason);
        row.status = nextStatus;
        alertSuccess(this.$t("updated_success") || "Status updated successfully");
      } catch (error) {
        event.target.value = row.status;
        alertError(error.response?.data?.message || this.$t("failed_update"));
      } finally {
        this.approvalLoading = { ...this.approvalLoading, [row.id]: false };
      }
    },

    async applyBulkStatus() {
  if (!this.bulkStatus) return;

  const { value: reason, isDismissed } = await Swal.fire({
    title:
      this.$i18n.locale === "ar"
        ? "سبب تغيير الحالة الجماعي"
        : "Reason for bulk status change",
    input: "text",
    inputPlaceholder:
      this.$i18n.locale === "ar"
        ? "اكتب السبب هنا..."
        : "Enter reason...",
    showCancelButton: true,
    confirmButtonText: this.$i18n.locale === "ar" ? "تأكيد" : "Confirm",
    cancelButtonText: this.$i18n.locale === "ar" ? "إلغاء" : "Cancel",
    confirmButtonColor: "#2563eb",
    cancelButtonColor: "#dc2626",
  });

  if (isDismissed) return;

  this.bulkLoading = true;

  try {
    const carIds = [...this.selectedCarIds];

    if (!carIds.length) {
      alertError(this.$t("no_data"));
      return;
    }

    const res = await carService.bulkChangeStatus(carIds, this.bulkStatus, reason);

    this.selectedCarIds = [];
    this.bulkStatus = "";
    this.selectionMode = false;

    await this.fetchCars(this.pagination?.current_page || 1);

    const batchData = res.data?.data;
    this.lastBatchUuid = batchData?.uuid || null;
    const updatedCount = batchData?.transitions?.length || carIds.length;
    this.lastBatchSkipped = carIds.length - updatedCount;

    const isAr = this.$i18n.locale === "ar";

    Swal.fire({
      icon: "success",
      title: isAr ? "تم تحديث الحالة" : "Status updated",
      html: `
        <div style="text-align:left;margin:12px 0;line-height:2">
          <span style="display:inline-block;background:#d1fae5;color:#065f46;padding:2px 12px;border-radius:6px;font-weight:700;font-size:14px">
            ${isAr ? `تم التحديث: ${updatedCount}` : `Updated: ${updatedCount}`}
          </span>
          ${this.lastBatchSkipped > 0 ? `
            <span style="display:inline-block;background:#fef3c7;color:#92400e;padding:2px 12px;border-radius:6px;font-weight:700;font-size:14px;margin-left:6px">
              ${isAr ? `تخطي: ${this.lastBatchSkipped}` : `Skipped: ${this.lastBatchSkipped}`}
            </span>
          ` : ''}
        </div>
        ${this.lastBatchUuid ? `
          <hr style="border:none;border-top:1px solid #e5e7eb;margin:12px 0">
          <button id="swal-undo-btn" style="width:100%;padding:10px;border-radius:8px;border:1px solid #fca5a5;background:#fff;color:#dc2626;font-weight:700;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px">
            <i class="fas fa-undo"></i> ${isAr ? "تراجع عن هذا الإجراء" : "Undo this action"}
          </button>
        ` : ''}
      `,
      didOpen: () => {
        const btn = document.getElementById('swal-undo-btn');
        if (btn) btn.onclick = () => { Swal.close(); this.confirmUndoBatch(); };
      },
      confirmButtonText: isAr ? "تم" : "Done",
      confirmButtonColor: "#2563eb",
    });
  } catch (error) {
    alertError(error.response?.data?.message || this.$t("failed_update"));
  } finally {
    this.bulkLoading = false;
  }
} ,

    async confirmUndoBatch() {
      if (!this.lastBatchUuid) return;

      const isAr = this.$i18n.locale === "ar";

      const { value: reason, isDismissed } = await Swal.fire({
        title: isAr ? "التراجع عن تغيير الحالة" : "Undo status change",
        html: isAr
          ? `<p style="color:#6b7280">سيتم إرجاع جميع السيارات إلى حالتها السابقة.</p>`
          : `<p style="color:#6b7280">All cars will be reverted to their previous state.</p>`,
        input: "text",
        inputPlaceholder: isAr ? "سبب التراجع (اختياري)..." : "Reason for undo (optional)...",
        showCancelButton: true,
        confirmButtonText: isAr ? "تراجع" : "Undo",
        confirmButtonColor: "#dc2626",
        cancelButtonText: isAr ? "إلغاء" : "Cancel",
        cancelButtonColor: "#6b7280",
      });

      if (isDismissed) return;

      try {
        await carService.undoBatch(this.lastBatchUuid, reason);

        this.lastBatchUuid = null;
        this.lastBatchSkipped = 0;

        await this.fetchCars(this.pagination?.current_page || 1);

        Swal.fire({
          icon: "success",
          title: isAr ? "تم التراجع بنجاح" : "Undo successful",
          text: isAr ? "تم إرجاع الحالة لجميع السيارات." : "All cars have been reverted.",
          confirmButtonColor: "#2563eb",
        });
      } catch (error) {
        alertError(error.response?.data?.message || (isAr ? "فشل التراجع" : "Failed to undo"));
      }
    } ,

    goToStatusHistory(row) {
      this.$router.push({
        name: "CarStatusHistory",
        params: { id: row.id },
      });
    },
    async exportToExcel() {
      this.exporting = true;

      try {
        // اجمع كل الصفحات المطابقة للفلاتر الحالية
        let allCars = [];
        let page = 1;
        let lastPage = 1;

        do {
          const res = await carService.getAll({
            per_page: 100, // زوّد الحجم عشان تقلل عدد الـ requests
            search: this.search,
            status: this.statusFilter || undefined,
            has_driver:
              this.hasDriverFilter !== "" ? this.hasDriverFilter : undefined,
            owner_id: this.ownerFilter !== "" ? this.ownerFilter : undefined,
            date_from: this.dateFrom || undefined,
            date_to: this.dateTo || undefined,
            price_min: this.priceMin !== "" ? this.priceMin : undefined,
            price_max: this.priceMax !== "" ? this.priceMax : undefined,
            all_cars: this.showAllCars || undefined,
            page,
            car_fields: this.car_fields,
          });

          const responseData = res.data;
          const pageData = responseData.data || [];
          allCars = allCars.concat(pageData);

          lastPage = responseData.pagination?.last_page || 1;
          page++;
        } while (page <= lastPage);

        if (allCars.length === 0) {
          alertError(this.$t("no_data"));
          return;
        }

        // جهّز البيانات بشكل مسطّح ومفهوم للإكسيل
        const rows = allCars.map((car) => ({
          ID: car.id,
          [this.$t("model_name")]: car.model_name,
          [this.$t("brand")]: car.brand?.name_en ?? "",
          [this.$t("category")]: car.category?.name_en ?? "",
          [this.$t("vehicle_type")]: car.vehicle_type?.name_en ?? "",
          [this.$t("year")]: car.year,
          [this.$t("owner")]: car.user?.username ?? "",
          [this.$t("status")]: car.status,
          [this.$t("plate_number")]: car.plate_number,
          ["Price/Day"]: car.price_per_day,
          ["Price/Week"]: car.price_per_week,
          ["Price/Month"]: car.price_per_month,
          Currency: car.currency,
          ["Has Driver"]: car.has_driver ? "Yes" : "No",
        }));

        // ابنِ ملف الإكسيل
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Cars");

        // ضبط عرض الأعمدة تلقائياً بشكل تقريبي
        const colWidths = Object.keys(rows[0]).map((key) => ({
          wch: Math.max(key.length, 15),
        }));
        worksheet["!cols"] = colWidths;

        const fileName = `cars_export_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        alertSuccess(this.$t("exported_success"));
      } catch (err) {
        console.error(err);
        alertError(this.$t("failed_export"));
      } finally {
        this.exporting = false;
      }
    },
 
  },

  async mounted() {
    this.paginationStore = usePaginationStore();
    const savedPage = this.paginationStore.getPage("vehicles") || 1;
    const startPage = savedPage;
 
    await Promise.all([this.fetchCars(startPage), this.fetchOwners()]);
 
  },
};
</script>
